import { useMemo, useState } from 'react';


import type { AdminStore } from '@/client/types/stores';
import { useStoreSettingsQuery } from '@/client/api/stores/stores.query';
import { useUpdateStoreProfile, useUpdateStoreSettings } from '@/client/api/stores/stores.mutation';
import { useClearStoreVenue, useSetStoreVenue } from '@/client/api/stores/parity.mutation';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';

import { SectionCard, CardSkeleton, FormSkeleton } from '../shared';
import { IdLookup } from '../../IdLookup';
import { ReasonDialog } from '../ReasonDialog';
import { AttributesSection } from './AttributesSection';
import { BranchesSection } from './BranchesSection';
import { FieldRow, SaveRow, Toggle } from './controls';

const DAYS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'],
  ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
] as const;
type DayKey = (typeof DAYS)[number][0];
type Slot = { open: string; close: string } | null;
interface Hours { timezone?: string | null; note?: string | null; weekly?: Partial<Record<DayKey, Slot>> | null }

const asHours = (raw: unknown): Hours => (raw && typeof raw === 'object' ? (raw as Hours) : {});

type Pickup = Record<string, unknown>;

/** `pickup_address` is a JSON object; legacy rows may hold a bare string. */
const asPickup = (raw: unknown): Pickup => {
  if (!raw) return {};
  if (typeof raw === 'string') return { type: 'reference', address: raw };
  return raw as Pickup;
};

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

const PickupCard: React.FC<{ store: AdminStore; pickup: Pickup }> = ({ store, pickup }) => {
  const initial = useMemo(
    () => ({ address: str(pickup.address), gpgpsCode: str(pickup.gpgpsCode) }),
    [pickup],
  );
  const [form, setForm] = useState(initial);
  const [confirm, setConfirm] = useState(false);
  const update = useUpdateStoreSettings();
  const canEdit = useGate(PERMISSIONS.store.settings).allowed;
  const dirty = form.address !== initial.address || form.gpgpsCode !== initial.gpgpsCode;

  const next = (): Pickup | null => {
    const address = form.address.trim();
    const gpgpsCode = form.gpgpsCode.trim().toUpperCase();
    if (!address && !gpgpsCode) return null;
    // Keep the pin/eid/lid the seller's picker stored; only the text changes.
    return {
      ...pickup,
      type: gpgpsCode && !address ? 'gpgps' : (pickup.type as string) || 'reference',
      address: address || null,
      gpgpsCode: gpgpsCode || null,
    };
  };

  return (
    <SectionCard title="Pickup location" description="Where buyers collect orders (store settings).">
      <div className="divide-y divide-gray-100">
        <FieldRow label="Address" hint="Street address or landmark shown to buyers.">
          <Input value={form.address} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="w-80" />
        </FieldRow>
        <FieldRow label="GhanaPost GPS" hint="Digital address, e.g. GA-123-4567.">
          <Input value={form.gpgpsCode} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, gpgpsCode: e.target.value }))} className="w-40" />
        </FieldRow>
      </div>
      {pickup.coordinates != null && (
        <p className="mt-2 text-xs text-gray-400">A map pin is set; it is kept when you edit the text.</p>
      )}
      <SaveRow dirty={dirty} saving={update.isPending} onSave={() => setConfirm(true)} onReset={() => setForm(initial)} />
      {confirm && (
        <ReasonDialog
          open
          title="Update pickup location"
          description={next() ? 'Buyers will see the new pickup details.' : 'Clears the pickup location.'}
          confirmLabel="Save"
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate(
              { storeid: store.storeid, pickup_address: next(), reason },
              { onSuccess: () => setConfirm(false) },
            )
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

/** Weekly opening hours, saved as `pickup_hours` through the seller's settings
 * rules (HH:MM slots; stored in the `operating_hours` attribute). */
const HoursCard: React.FC<{ store: AdminStore; hours: Hours }> = ({ store, hours }) => {
  const initial = useMemo(
    () => Object.fromEntries(DAYS.map(([k]) => [k, hours.weekly?.[k] ?? null])) as Record<DayKey, Slot>,
    [hours],
  );
  const [weekly, setWeekly] = useState(initial);
  const [note, setNote] = useState(hours.note ?? '');
  const [confirm, setConfirm] = useState(false);
  const update = useUpdateStoreSettings();
  const canEdit = useGate(PERMISSIONS.store.settings).allowed;
  const dirty = JSON.stringify(weekly) !== JSON.stringify(initial) || note !== (hours.note ?? '');
  const valid = Object.values(weekly).every((s) => !s || (/^\d{2}:\d{2}$/.test(s.open) && /^\d{2}:\d{2}$/.test(s.close)));

  const setDay = (k: DayKey, slot: Slot) => setWeekly((w) => ({ ...w, [k]: slot }));

  return (
    <SectionCard title="Opening hours" description="When buyers can collect or visit.">
      <div className="divide-y divide-gray-100">
        {DAYS.map(([k, name]) => {
          const slot = weekly[k];
          return (
            <div key={k} className="flex items-center gap-3 py-2 text-sm">
              <span className="w-24 font-medium text-gray-900">{name}</span>
              <Toggle disabled={!canEdit} checked={!!slot} onChange={(on) => setDay(k, on ? { open: '09:00', close: '18:00' } : null)} />
              {slot ? (
                <span className="flex items-center gap-1">
                  <Input disabled={!canEdit} type="time" value={slot.open} onChange={(e) => setDay(k, { ...slot, open: e.target.value })} className="h-8 w-28" />
                  <span className="text-gray-400">–</span>
                  <Input disabled={!canEdit} type="time" value={slot.close} onChange={(e) => setDay(k, { ...slot, close: e.target.value })} className="h-8 w-28" />
                </span>
              ) : (
                <span className="text-gray-400">Closed</span>
              )}
            </div>
          );
        })}
      </div>
      <FieldRow label="Note" hint="e.g. Closed on public holidays">
        <Input disabled={!canEdit} value={note} onChange={(e) => setNote(e.target.value)} className="w-72" />
      </FieldRow>
      <SaveRow dirty={dirty && valid} saving={update.isPending} onSave={() => setConfirm(true)}
        onReset={() => { setWeekly(initial); setNote(hours.note ?? ''); }} />
      {confirm && (
        <ReasonDialog
          open
          title="Update opening hours"
          description="Shown on the storefront and at checkout pickup."
          confirmLabel="Save"
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate(
              {
                storeid: store.storeid,
                reason,
                pickup_hours: { timezone: hours.timezone ?? 'Africa/Accra', note: note.trim() || null, weekly },
              },
              { onSuccess: () => setConfirm(false) },
            )
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

/** The place listing the store is linked to (its physical venue). */
const VenueCard: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [eid, setEid] = useState('');
  const [allowClaimed, setAllowClaimed] = useState(false);
  const [dialog, setDialog] = useState<null | 'link' | 'unlink'>(null);
  const link = useSetStoreVenue();
  const unlink = useClearStoreVenue();

  return (
    <SectionCard title="Venue" description={store.eid ? `Linked to place #${store.eid}` : 'Not linked to a place listing.'}>
      <div className="flex flex-wrap items-start gap-2">
        <IdLookup kind="listing" value={eid} onChange={setEid} className="w-72" placeholder="Search place by name, or type an eid" />
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={allowClaimed} onChange={(e) => setAllowClaimed(e.target.checked)} />
          Move even if another store claims it
        </label>
        <PermissionButton code={PERMISSIONS.store.profile} size="sm" disabled={!eid} onClick={() => setDialog('link')}>Link venue</PermissionButton>
        {store.eid ? (
          <PermissionButton code={PERMISSIONS.store.profile} size="sm" variant="outline" onClick={() => setDialog('unlink')}>Unlink</PermissionButton>
        ) : null}
      </div>
      {dialog && (
        <ReasonDialog
          open
          title={dialog === 'link' ? `Link place #${eid}` : 'Unlink venue'}
          description={dialog === 'link' ? 'The place is marked claimed by this store; any previous venue is released.' : 'The place is released.'}
          confirmLabel={dialog === 'link' ? 'Link' : 'Unlink'}
          destructive={dialog === 'unlink'}
          reasonRequired
          loading={link.isPending || unlink.isPending}
          onConfirm={(reason) =>
            dialog === 'link'
              ? link.mutate({ storeid: store.storeid, eid: Number(eid), allow_claimed: allowClaimed, reason }, { onSuccess: () => { setDialog(null); setEid(''); } })
              : unlink.mutate({ storeid: store.storeid, reason }, { onSuccess: () => setDialog(null) })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </SectionCard>
  );
};

const TradingLocationCard: React.FC<{ store: AdminStore }> = ({ store }) => {
  const initial = store.lid != null ? String(store.lid) : '';
  const [lid, setLid] = useState(initial);
  const [confirm, setConfirm] = useState(false);
  const update = useUpdateStoreProfile();
  const canEdit = useGate(PERMISSIONS.store.profile).allowed;

  return (
    <SectionCard title="Trading location" description="The store's area (location id). Listings inherit it for search and “near me”.">
      <FieldRow label="Location">
        <IdLookup kind="location" value={lid} disabled={!canEdit} onChange={setLid} className="w-64" />
      </FieldRow>
      <SaveRow dirty={lid !== initial} saving={update.isPending} onSave={() => setConfirm(true)} onReset={() => setLid(initial)} />
      {confirm && (
        <ReasonDialog
          open
          title="Change trading location"
          description={lid ? `Sets lid ${lid}.` : 'Clears the trading location.'}
          confirmLabel="Save"
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate({ storeid: store.storeid, lid: lid ? Number(lid) : null, reason }, { onSuccess: () => setConfirm(false) })
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

export const VisitSection: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data: settings, isLoading } = useStoreSettingsQuery(store.storeid);
  const pickup = useMemo(() => asPickup(settings?.pickup_address), [settings?.pickup_address]);
  if (isLoading)
    return (
      <div className="space-y-4">
        <CardSkeleton description><FormSkeleton fields={4} /></CardSkeleton>
        <CardSkeleton description><FormSkeleton fields={2} /></CardSkeleton>
      </div>
    );

  return (
    <div className="space-y-4">
      <PickupCard key={JSON.stringify(pickup)} store={store} pickup={pickup} />
      <TradingLocationCard key={`lid-${store.lid ?? ''}`} store={store} />

      <HoursCard key={JSON.stringify(settings?.pickup_hours ?? null)} store={store} hours={asHours(settings?.pickup_hours)} />
      <VenueCard store={store} />
      <BranchesSection store={store} />
      <AttributesSection store={store} groups={['visit']} title="Address details" description="Physical and postal address, GhanaPost GPS and map links." />
    </div>
  );
};
